import { useState, useEffect } from 'react';
import API from '../api';

const fmt   = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const fmtN  = v => new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0 }).format(v || 0);

const BIMESTRES = [
  { num: 1, label: 'B1', meses: 'Ene — Feb' },
  { num: 2, label: 'B2', meses: 'Mar — Abr' },
  { num: 3, label: 'B3', meses: 'May — Jun' },
  { num: 4, label: 'B4', meses: 'Jul — Ago' },
  { num: 5, label: 'B5', meses: 'Sep — Oct' },
  { num: 6, label: 'B6', meses: 'Nov — Dic' },
];

function downloadCSV(csv, filename) {
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

function toCSVRow(row) {
  return row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',');
}

export default function IVA() {
  const [anio,      setAnio]      = useState(new Date().getFullYear());
  const [bimestre,  setBimestre]  = useState(Math.ceil((new Date().getMonth() + 1) / 2));
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);

  useEffect(() => {
    setLoading(true);
    API.get(`/iva-resumen?anio=${anio}&bimestre=${bimestre}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [anio, bimestre]);

  // Construye mapa bimestre → { generado, descontable, saldo }
  const bimMap = {};
  if (data) {
    data.porBim.forEach(r => {
      if (!bimMap[r.bim]) bimMap[r.bim] = {};
      bimMap[r.bim].generado = parseFloat(r.generado);
    });
    data.porBimGas.forEach(r => {
      if (!bimMap[r.bim]) bimMap[r.bim] = {};
      bimMap[r.bim].descontable = parseFloat(r.descontable);
    });
    (data.porBimRete || []).forEach(r => {
      if (!bimMap[r.bim]) bimMap[r.bim] = {};
      bimMap[r.bim].reteiva = parseFloat(r.reteiva);
    });
  }

  function exportar() {
    if (!data) return;
    const b = BIMESTRES[bimestre - 1];
    const rows = [
      ['DECLARACIÓN IVA', `B${bimestre} ${b.meses} ${anio}`, '', '', ''],
      ['', '', '', '', ''],
      ['IVA GENERADO (VENTAS)', '', '', fmt(data.iva_generado), ''],
      ['IVA DESCONTABLE (COMPRAS)', '', '', fmt(data.iva_descontable), ''],
      ['RETEIVA RETENIDO (A FAVOR)', '', '', fmt(data.reteiva_total), ''],
      ['SALDO A PAGAR DIAN', '', '', fmt(Math.max(0, data.saldo)), ''],
      ['', '', '', '', ''],
      ['FACTURAS CON IVA', '', '', '', ''],
      ['Fecha', 'Cliente', 'Servicio', 'Base', 'IVA Generado'],
      ...data.detalle_ingresos.map(r => [r.fecha?.slice(0,10), r.cliente, r.servicio, r.valor, r.valor_iva]),
      ['', '', '', '', ''],
      ['GASTOS CON IVA DEDUCIBLE', '', '', '', ''],
      ['Fecha', 'Descripción', 'Categoría', 'Base', 'IVA Descontable'],
      ...data.detalle_gastos.map(g => [g.fecha?.slice(0,10), g.descripcion, g.categoria, g.valor, g.iva_valor]),
      ['', '', '', '', ''],
      ['RETEIVA RETENIDO POR CLIENTES', '', '', '', ''],
      ['Fecha', 'Cliente', 'Servicio', 'Base + IVA', 'ReteIVA retenido'],
      ...data.detalle_reteiva.map(r => [r.fecha?.slice(0,10), r.cliente, r.servicio, r.valor, r.reteiva_valor]),
    ];
    downloadCSV(rows.map(toCSVRow).join('\n'), `IVA_B${bimestre}_${b.meses.replace(' — ', '-')}_${anio}.csv`);
  }

  const saldo = data ? Math.max(0, data.saldo) : 0;
  const saldoFavor = data && data.saldo < 0;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#08293f', margin: 0 }}>IVA — Declaraciones</h1>
          <p style={{ fontSize: 11, color: '#5a7a8a', letterSpacing: '0.06em', marginTop: 4 }}>Bimestral · Responsable de IVA desde Abr 2026</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={anio} onChange={e => setAnio(parseInt(e.target.value))} className="input" style={{ background: '#f4f7fa', border: '1px solid #e8eef2', color: '#08293f', width: 'auto', fontSize: 12 }}>
            <option value="2026">2026</option>
            <option value="2025">2025</option>
          </select>
          <button onClick={exportar} disabled={!data} className="btn-dark" style={{ fontSize: 12, letterSpacing: '0.04em' }}>
            ↓ Exportar CSV
          </button>
        </div>
      </div>

      {/* Selector bimestre */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
        {BIMESTRES.map(b => (
          <button key={b.num} onClick={() => setBimestre(b.num)} style={{
            padding: '8px 14px', borderRadius: 2, cursor: 'pointer', fontSize: 12, fontWeight: 600,
            fontFamily: "'Space Grotesk',sans-serif", letterSpacing: '0.04em',
            background: bimestre === b.num ? '#08293f' : '#fff',
            color: bimestre === b.num ? '#00b8e3' : '#5a7a8a',
            border: bimestre === b.num ? '1px solid rgba(0,184,227,0.3)' : '1px solid #e8eef2',
          }}>
            <div style={{ fontSize: 11 }}>{b.label}</div>
            <div style={{ fontSize: 9, fontWeight: 400, marginTop: 1 }}>{b.meses}</div>
          </button>
        ))}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '40px 0', color: '#5a7a8a', fontSize: 13 }}>Calculando...</div>}

      {data && !loading && (
        <>
          {/* ── Las 3 opciones ── */}
          <div className="iva-kpi-grid" style={{ marginBottom: 20 }}>
            {/* 1. IVA Generado */}
            <div style={{ background: '#08293f', border: '1px solid rgba(0,184,227,0.25)', borderRadius: 2, padding: '18px 20px' }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(0,184,227,0.6)', marginBottom: 8 }}>① IVA Generado</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#00b8e3', lineHeight: 1 }}>{fmt(data.iva_generado)}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>
                Base: {fmt(data.base_generado)} · {data.facturas} factura{data.facturas !== 1 ? 's' : ''}
              </div>
            </div>

            {/* 2. IVA Descontable */}
            <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '18px 20px' }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 8 }}>② IVA Descontable</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#059669', lineHeight: 1 }}>{fmt(data.iva_descontable)}</div>
              <div style={{ fontSize: 11, color: '#b3b3b3', marginTop: 6 }}>
                Base: {fmt(data.base_descontable)} · {data.recibos} gasto{data.recibos !== 1 ? 's' : ''}
              </div>
            </div>

            {/* 3. ReteIVA retenido */}
            <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '18px 20px' }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 8 }}>③ ReteIVA retenido (a favor)</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#059669', lineHeight: 1 }}>{fmt(data.reteiva_total)}</div>
              <div style={{ fontSize: 11, color: '#b3b3b3', marginTop: 6 }}>
                Retenido por clientes al pagar · {data.reteiva_registros} registro{data.reteiva_registros !== 1 ? 's' : ''}
              </div>
            </div>

            {/* 4. Saldo a pagar */}
            <div style={{
              background: saldoFavor ? 'rgba(5,150,105,0.06)' : 'rgba(220,38,38,0.06)',
              border: `1px solid ${saldoFavor ? 'rgba(5,150,105,0.2)' : 'rgba(220,38,38,0.2)'}`,
              borderRadius: 2, padding: '18px 20px'
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: saldoFavor ? '#059669' : '#dc2626', marginBottom: 8 }}>
                ④ {saldoFavor ? 'Saldo a favor' : 'Saldo a pagar DIAN'}
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: saldoFavor ? '#059669' : '#dc2626', lineHeight: 1 }}>
                {fmt(saldoFavor ? Math.abs(data.saldo) : saldo)}
              </div>
              <div style={{ fontSize: 11, color: saldoFavor ? '#059669' : '#dc2626', marginTop: 6, opacity: 0.7 }}>
                {saldoFavor ? 'IVA descontable + ReteIVA mayor al generado' : 'IVA Generado − IVA Descontable − ReteIVA'}
              </div>
            </div>
          </div>

          {/* Resumen anual por bimestre */}
          <div className="card" style={{ marginBottom: 16, padding: 0, overflowX: 'auto' }}>
            <div style={{ padding: '12px 16px', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a' }}>
              — Resumen anual {anio}
            </div>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Bimestre</th><th>Período</th>
                  <th style={{ textAlign: 'right' }}>IVA Generado</th>
                  <th style={{ textAlign: 'right' }}>IVA Descontable</th>
                  <th style={{ textAlign: 'right' }}>ReteIVA</th>
                  <th style={{ textAlign: 'right' }}>Saldo</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {BIMESTRES.map(b => {
                  const gen  = bimMap[b.num]?.generado     || 0;
                  const des  = bimMap[b.num]?.descontable  || 0;
                  const rete = bimMap[b.num]?.reteiva      || 0;
                  const sal  = gen - des - rete;
                  const isActive = b.num === bimestre;
                  return (
                    <tr key={b.num} onClick={() => setBimestre(b.num)} style={{ cursor: 'pointer',
                      background: isActive ? 'rgba(0,184,227,0.04)' : undefined }}>
                      <td style={{ fontWeight: 700, color: isActive ? '#00b8e3' : '#08293f' }}>{b.label}</td>
                      <td style={{ color: '#5a7a8a', fontSize: 12 }}>{b.meses} {anio}</td>
                      <td style={{ textAlign: 'right', color: '#08293f', fontWeight: gen > 0 ? 700 : 400 }}>{gen > 0 ? fmt(gen) : '—'}</td>
                      <td style={{ textAlign: 'right', color: '#059669', fontWeight: des > 0 ? 600 : 400 }}>{des > 0 ? fmt(des) : '—'}</td>
                      <td style={{ textAlign: 'right', color: '#059669', fontWeight: rete > 0 ? 600 : 400 }}>{rete > 0 ? fmt(rete) : '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700,
                        color: sal < 0 ? '#059669' : sal > 0 ? '#dc2626' : '#b3b3b3' }}>
                        {gen > 0 || des > 0 || rete > 0 ? fmt(Math.abs(sal)) : '—'}
                      </td>
                      <td>
                        {gen === 0 && des === 0 && rete === 0
                          ? <span style={{ fontSize: 10, color: '#b3b3b3' }}>Sin datos</span>
                          : <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 2,
                              background: sal < 0 ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.08)',
                              color: sal < 0 ? '#059669' : '#dc2626' }}>
                              {sal < 0 ? 'A FAVOR' : 'A PAGAR'}
                            </span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Detalle: dos columnas */}
          <div className="iva-detalle-grid">
            {/* IVA Generado — facturas */}
            <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
              <div style={{ padding: '12px 16px', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>— Facturas con IVA · B{bimestre}</span>
                <span style={{ color: '#00b8e3', fontWeight: 700 }}>{fmt(data.iva_generado)}</span>
              </div>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Fecha</th><th>Cliente</th>
                    <th style={{ textAlign: 'right' }}>Base</th>
                    <th style={{ textAlign: 'right' }}>IVA 19%</th>
                  </tr>
                </thead>
                <tbody>
                  {data.detalle_ingresos.map(r => (
                    <tr key={r.id}>
                      <td style={{ color: '#5a7a8a', fontSize: 12, whiteSpace: 'nowrap' }}>{r.fecha?.slice(0,10)}</td>
                      <td style={{ fontWeight: 600, color: '#08293f', fontSize: 12 }}>{r.cliente}</td>
                      <td style={{ textAlign: 'right', color: '#5a7a8a' }}>{fmt(r.valor)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#00b8e3' }}>{fmt(r.valor_iva)}</td>
                    </tr>
                  ))}
                  {!data.detalle_ingresos.length && (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: '20px 0', color: '#b3b3b3', fontSize: 12 }}>
                      Sin facturas con IVA en este bimestre
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* IVA Descontable — gastos */}
            <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
              <div style={{ padding: '12px 16px', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>— Gastos con IVA · B{bimestre}</span>
                <span style={{ color: '#059669', fontWeight: 700 }}>{fmt(data.iva_descontable)}</span>
              </div>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Fecha</th><th>Descripción</th>
                    <th style={{ textAlign: 'right' }}>Base</th>
                    <th style={{ textAlign: 'right' }}>IVA desc.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.detalle_gastos.map(g => (
                    <tr key={g.id}>
                      <td style={{ color: '#5a7a8a', fontSize: 12, whiteSpace: 'nowrap' }}>{g.fecha?.slice(0,10)}</td>
                      <td style={{ fontWeight: 600, color: '#08293f', fontSize: 12 }}>{g.descripcion}</td>
                      <td style={{ textAlign: 'right', color: '#5a7a8a' }}>{fmt(g.valor)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmt(g.iva_valor)}</td>
                    </tr>
                  ))}
                  {!data.detalle_gastos.length && (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: '20px 0', color: '#b3b3b3', fontSize: 12 }}>
                      Sin gastos con IVA en este bimestre
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ReteIVA — retenido por clientes */}
            <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
              <div style={{ padding: '12px 16px', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>— ReteIVA retenido · B{bimestre}</span>
                <span style={{ color: '#059669', fontWeight: 700 }}>{fmt(data.reteiva_total)}</span>
              </div>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Fecha</th><th>Cliente</th>
                    <th style={{ textAlign: 'right' }}>Base + IVA</th>
                    <th style={{ textAlign: 'right' }}>Retenido</th>
                  </tr>
                </thead>
                <tbody>
                  {data.detalle_reteiva.map(r => (
                    <tr key={r.id}>
                      <td style={{ color: '#5a7a8a', fontSize: 12, whiteSpace: 'nowrap' }}>{r.fecha?.slice(0,10)}</td>
                      <td style={{ fontWeight: 600, color: '#08293f', fontSize: 12 }}>{r.cliente}</td>
                      <td style={{ textAlign: 'right', color: '#5a7a8a' }}>{fmt((r.factura_base_ss || r.valor) + (r.valor_iva || 0))}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmt(r.reteiva_valor)}</td>
                    </tr>
                  ))}
                  {!data.detalle_reteiva.length && (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: '20px 0', color: '#b3b3b3', fontSize: 12 }}>
                      Sin ReteIVA en este bimestre
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <style>{`
        .iva-kpi-grid    { display: grid; grid-template-columns: repeat(4,1fr); gap: 10px; }
        .iva-detalle-grid{ display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-top: 12px; }
        @media (max-width: 1024px) {
          .iva-kpi-grid     { grid-template-columns: repeat(2,1fr); }
          .iva-detalle-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .iva-kpi-grid     { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}
